import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    ArrowLeft,
    Package,
    Plus,
    X,
    Loader2,
    CheckCircle2,
    Camera,
    UserCheck,
    Trash2
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import api from '../../services/api';

const CreateEntity = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);

    // Entity form data
    const [entityForm, setEntityForm] = useState({
        entityName: '',
        description: '',
        isActive: true
    });

    // Checklist items array
    const [checklistItems, setChecklistItems] = useState([
        {
            id: 1,
            itemName: '',
            description: '',
            groupName: 'General',
            isRequired: true,
            requiresPhoto: false,
            requiresSupervisorApproval: false,
            isActive: true
        }
    ]);

    const [errors, setErrors] = useState({});

    const addChecklistItem = () => {
        const newId = checklistItems.length > 0 ? Math.max(...checklistItems.map(i => i.id)) + 1 : 1;
        setChecklistItems([
            ...checklistItems,
            {
                id: newId,
                itemName: '',
                description: '',
                groupName: 'General',
                isRequired: true,
                requiresPhoto: false,
                requiresSupervisorApproval: false,
                isActive: true
            }
        ]);
    };

    const removeChecklistItem = (id) => {
        if (checklistItems.length === 1) {
            toast({
                title: 'Cannot Remove',
                description: 'At least one checklist item is required',
                variant: 'destructive'
            });
            return;
        }
        setChecklistItems(checklistItems.filter(item => item.id !== id));
    };

    const updateChecklistItem = (id, field, value) => {
        setChecklistItems(checklistItems.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate entity
        if (!entityForm.entityName.trim()) {
            newErrors.entityName = 'Entity name is required';
        }

        // Validate checklist items - at least one must have itemName
        const validItems = checklistItems.filter(item => item.itemName.trim());
        if (validItems.length === 0) {
            newErrors.checklistItems = 'At least one checklist item with a name is required';
            toast({
                title: 'Validation Error',
                description: 'Please add at least one checklist item with a name',
                variant: 'destructive'
            });
        }

        // Validate each filled checklist item
        checklistItems.forEach((item, index) => {
            if (item.itemName.trim()) {
                // Group name is required
                if (!item.groupName || !item.groupName.trim()) {
                    newErrors[`checklist_${item.id}_groupName`] = 'Group name is required';
                }
            } else if (checklistItems.length > 1) {
                // Multiple items but this one is empty - that's ok, will be filtered
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast({
                title: 'Validation Error',
                description: 'Please fix all errors before submitting',
                variant: 'destructive'
            });

            // Scroll to first error
            const firstErrorKey = Object.keys(errors)[0];
            const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
            }
            return;
        }

        setSubmitting(true);

        try {
            // Filter valid checklist items (with names)
            const validChecklistItems = checklistItems.filter(item => item.itemName.trim());

            // Create entity with checklist items in a single transaction
            const createdEntity = await api.createEntityWithChecklist({
                entityName: entityForm.entityName,
                description: entityForm.description,
                isActive: entityForm.isActive,
                checklistItems: validChecklistItems.map((item) => ({
                    itemName: item.itemName,
                    description: item.description || null,
                    groupName: item.groupName || null,
                    isRequired: item.isRequired,
                    requiresPhoto: item.requiresPhoto,
                    requiresSupervisorApproval: item.requiresSupervisorApproval,
                    isActive: item.isActive
                }))
            });

            toast({
                title: 'Success',
                description: `Entity "${createdEntity.entityName}" created with ${validChecklistItems.length} checklist items`,
            });

            // Navigate back to entity list
            navigate('/admin/settings/entity-checklist');

        } catch (error) {
            console.error('Error creating entity:', error);
            toast({
                title: 'Error',
                description: error.message || 'Failed to create entity',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Toaster />

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/settings/entity-checklist')}>
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shrink-0">
                                <Package className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create New Entity</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Add a new installation entity with its checklist items</p>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Entity Details Card */}
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>Entity Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="entityName">
                                        Entity Name <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="entityName"
                                        name="entityName"
                                        value={entityForm.entityName}
                                        onChange={(e) => setEntityForm({ ...entityForm, entityName: e.target.value })}
                                        placeholder="e.g., Kitchen Wall Unit, Wardrobe, Vanity"
                                        className={errors.entityName ? 'border-red-500' : ''}
                                    />
                                    {errors.entityName && (
                                        <p className="text-sm text-red-500 mt-1">{errors.entityName}</p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={entityForm.description}
                                        onChange={(e) => setEntityForm({ ...entityForm, description: e.target.value })}
                                        placeholder="Brief description of the entity type"
                                        rows={3}
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={entityForm.isActive}
                                        onChange={(e) => setEntityForm({ ...entityForm, isActive: e.target.checked })}
                                        className="rounded h-4 w-4 text-teal-600"
                                    />
                                    <Label htmlFor="isActive" className="ml-2 cursor-pointer">
                                        Active (entity will be available for use)
                                    </Label>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Checklist Items Card */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Checklist Items ({checklistItems.length})</CardTitle>
                                    <p className="text-sm text-gray-600 mt-1">Define installation steps for this entity</p>
                                </div>
                                <Button
                                    type="button"
                                    onClick={addChecklistItem}
                                    variant="outline"
                                    size="sm"
                                    className="border-teal-600 text-teal-600 hover:bg-teal-50"
                                >
                                    <Plus className="h-4 w-4 mr-1" /> Add Item
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {checklistItems.map((item, index) => (
                                <div
                                    key={item.id}
                                    className="p-4 border border-gray-200 rounded-lg bg-white space-y-4"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-sm font-semibold">
                                                {index + 1}
                                            </span>
                                            <h3 className="font-semibold text-gray-900">Checklist Item {index + 1}</h3>
                                        </div>
                                        {checklistItems.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeChecklistItem(item.id)}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <Label htmlFor={`itemName_${item.id}`}>
                                                Item Name <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id={`itemName_${item.id}`}
                                                name={`checklist_${item.id}_itemName`}
                                                value={item.itemName}
                                                onChange={(e) => updateChecklistItem(item.id, 'itemName', e.target.value)}
                                                placeholder="e.g., Check wall alignment, Install brackets"
                                                className={errors[`checklist_${item.id}_itemName`] ? 'border-red-500' : ''}
                                            />
                                            {errors[`checklist_${item.id}_itemName`] && (
                                                <p className="text-sm text-red-500 mt-1">{errors[`checklist_${item.id}_itemName`]}</p>
                                            )}
                                        </div>

                                        <div>
                                            <Label htmlFor={`description_${item.id}`}>Description</Label>
                                            <Textarea
                                                id={`description_${item.id}`}
                                                name={`checklist_${item.id}_description`}
                                                value={item.description}
                                                onChange={(e) => updateChecklistItem(item.id, 'description', e.target.value)}
                                                placeholder="Detailed instructions for this step"
                                                rows={2}
                                                className={errors[`checklist_${item.id}_description`] ? 'border-red-500' : ''}
                                            />
                                            {errors[`checklist_${item.id}_description`] && (
                                                <p className="text-sm text-red-500 mt-1">{errors[`checklist_${item.id}_description`]}</p>
                                            )}
                                        </div>

                                        <div>
                                            <Label htmlFor={`groupName_${item.id}`}>
                                                Group Name <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id={`groupName_${item.id}`}
                                                name={`checklist_${item.id}_groupName`}
                                                value={item.groupName || ''}
                                                onChange={(e) => updateChecklistItem(item.id, 'groupName', e.target.value)}
                                                placeholder="e.g., Material Checklist, Installation, General"
                                                required
                                                className={errors[`checklist_${item.id}_groupName`] ? 'border-red-500' : ''}
                                            />
                                            {errors[`checklist_${item.id}_groupName`] && (
                                                <p className="text-sm text-red-500 mt-1">{errors[`checklist_${item.id}_groupName`]}</p>
                                            )}
                                            <p className="text-xs text-gray-500 mt-1">Items are displayed grouped by name</p>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:col-span-2">
                                        </div>

                                        <div className="md:col-span-2 space-y-2 pt-2 border-t">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={`isRequired_${item.id}`}
                                                    checked={item.isRequired}
                                                    onChange={(e) => updateChecklistItem(item.id, 'isRequired', e.target.checked)}
                                                    className="rounded h-4 w-4 text-teal-600"
                                                />
                                                <Label htmlFor={`isRequired_${item.id}`} className="cursor-pointer">
                                                    Required (mandatory installation step)
                                                </Label>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={`requiresPhoto_${item.id}`}
                                                    checked={item.requiresPhoto}
                                                    onChange={(e) => updateChecklistItem(item.id, 'requiresPhoto', e.target.checked)}
                                                    className="rounded h-4 w-4 text-teal-600"
                                                />
                                                <Label htmlFor={`requiresPhoto_${item.id}`} className="cursor-pointer flex items-center gap-1">
                                                    <Camera className="h-4 w-4 text-blue-600" />
                                                    Requires Photo (installer must upload photo proof)
                                                </Label>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={`requiresSupervisorApproval_${item.id}`}
                                                    checked={item.requiresSupervisorApproval}
                                                    onChange={(e) => updateChecklistItem(item.id, 'requiresSupervisorApproval', e.target.checked)}
                                                    className="rounded h-4 w-4 text-teal-600"
                                                />
                                                <Label htmlFor={`requiresSupervisorApproval_${item.id}`} className="cursor-pointer flex items-center gap-1">
                                                    <UserCheck className="h-4 w-4 text-purple-600" />
                                                    Requires Supervisor Approval
                                                </Label>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={`isActive_${item.id}`}
                                                    checked={item.isActive}
                                                    onChange={(e) => updateChecklistItem(item.id, 'isActive', e.target.checked)}
                                                    className="rounded h-4 w-4 text-teal-600"
                                                />
                                                <Label htmlFor={`isActive_${item.id}`} className="cursor-pointer">
                                                    Active
                                                </Label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {checklistItems.length === 0 && (
                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                    <p className="text-gray-600 mb-3">No checklist items added yet</p>
                                    <Button
                                        type="button"
                                        onClick={addChecklistItem}
                                        variant="outline"
                                        className="border-teal-600 text-teal-600 hover:bg-teal-50"
                                    >
                                        <Plus className="h-4 w-4 mr-2" /> Add First Item
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex gap-4 mt-6 sticky bottom-4 bg-white p-4 rounded-lg shadow-lg border border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/admin/settings/entity-checklist')}
                            className="flex-1"
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 bg-teal-600 hover:bg-teal-700"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating Entity...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Create Entity & Checklist
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateEntity;
